/**
 * TanStack Query hook for fetching all lobby data in a single request.
 *
 * This unified endpoint returns everything needed for the lobby/home page:
 * - Categories with game counts + pre-grouped games per category
 * - Latest winners for the winners carousel
 * - Provider names (for sidebar filter)
 * - Live categories with game counts
 * - Jackpot amounts and configs
 * - Live games enabled flag
 *
 * Previously this was split across /game/lobby and /lobby/init — now consolidated.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Game } from '@/data/gamesDatabase';

export interface LobbyCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  display_order: number;
  section: string;
  game_count: number;
  reduced_redeemable?: boolean;
}

export interface LobbyGame {
  id: string;
  name: string;
  external_game_id: string;
  external_image_url: string;
  custom_image_url?: string;
  use_custom_image?: boolean;
  provider_name?: string;
  aggregator_name?: string;
}

export interface LobbyWinner {
  id: string;
  playerName: string;
  gameName: string;
  gameImage: string;
  winAmount: number;
  currency: 'GC' | 'SC';
  spinAmount: number;
  winType: string | null;
  gameId: number | null;
  gameExternalId: string | null;
  aggregatorName: string | null;
  providerName: string | null;
  externalSessionId: string | null;
  timestamp: Date | string;
}

export interface LobbyLiveCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  image_url?: string;
  display_order: number;
  game_count: number;
}

export interface JackpotAmount {
  name: string;
  amount: number;
  currency: 'GC' | 'SC';
  threshold?: number;
}

export interface JackpotConfig {
  id: number;
  name: string;
  display_name: string;
  currency: 'GC' | 'SC';
  contribution_percentage: number;
  is_active: boolean;
}

export interface LobbyDataResponse {
  success: boolean;
  categories: LobbyCategory[];
  allCategories: LobbyCategory[];
  gamesByCategory: Record<string, LobbyGame[]>;
  latestWinners: LobbyWinner[];
  providers: string[];
  liveCategories: LobbyLiveCategory[];
  jackpot: { amounts: JackpotAmount[]; configs: JackpotConfig[] };
  liveGamesEnabled: boolean;
  cachedAt?: string;
}

export interface LobbyData {
  categories: LobbyCategory[];
  allCategories: LobbyCategory[];
  gamesByCategory: Record<string, Game[]>;
  latestWinners: LobbyWinner[];
  providers: string[];
  liveCategories: LobbyLiveCategory[];
  jackpot: { amounts: JackpotAmount[]; configs: JackpotConfig[] };
  liveGamesEnabled: boolean;
  cachedAt?: string;
}

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Transform lobby game data to the UI Game format
 */
const transformLobbyGameToUI = (game: LobbyGame): Game => {
  return {
    id: game.id,
    title: game.name,
    image: game.external_image_url || '/placeholder.svg',
    provider: game.provider_name || 'Unknown',
    category: 'slots',
    isNew: false,
    isHot: false,
    isExclusive: false,
  };
};

/**
 * Fetch all lobby data in a single optimized request.
 *
 * @param section - 'casino' (default) or 'high_play'
 * @param gamesPerCategory - Number of games to fetch per category (default 50)
 */
export function useLobbyDataQuery(section: string = 'casino', gamesPerCategory: number = 50) {
  return useQuery({
    queryKey: ['lobby-data', section, gamesPerCategory],
    queryFn: async (): Promise<LobbyData> => {
      const params = new URLSearchParams({
        section,
        gamesPerCategory: String(gamesPerCategory),
        winnersLimit: '30',
      });

      const response = await apiClient.get<LobbyDataResponse>(`/game/lobby?${params.toString()}`);

      if (!response?.success) {
        return {
          categories: [],
          allCategories: [],
          gamesByCategory: {},
          latestWinners: [],
          providers: [],
          liveCategories: [],
          jackpot: { amounts: [], configs: [] },
          liveGamesEnabled: true,
        };
      }

      // Transform games to UI format
      const transformedGamesByCategory: Record<string, Game[]> = {};
      for (const [slug, games] of Object.entries(response.gamesByCategory || {})) {
        transformedGamesByCategory[slug] = games.map(transformLobbyGameToUI);
      }

      return {
        categories: response.categories || [],
        allCategories: response.allCategories || [],
        gamesByCategory: transformedGamesByCategory,
        latestWinners: response.latestWinners || [],
        providers: response.providers || [],
        liveCategories: response.liveCategories || [],
        jackpot: response.jackpot || { amounts: [], configs: [] },
        liveGamesEnabled: response.liveGamesEnabled ?? true,
        cachedAt: response.cachedAt,
      };
    },
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES * 2,
  });
}

/**
 * Get games for a specific category from the lobby data.
 * This is a helper to extract category-specific games from the unified response.
 */
export function useLobbyGamesForCategory(categorySlug: string, section: string = 'casino') {
  const { data, isLoading, error } = useLobbyDataQuery(section);

  return {
    data: data?.gamesByCategory[categorySlug] || [],
    isLoading,
    error,
  };
}
